'use client';

import type { TopNavigationRightButtonProps } from '@yanolja-rnd/nol-design-system';
import type { CategoryType } from '@/app/(pages)/results/type';
import { TopNavigation } from '@yanolja-rnd/nol-design-system';
import { IcArrowback, IcHome, IcDeleteFill } from '@yanolja-rnd/nol-icons';
import { yaNative } from '@weberus/ui/utils/ya-native';
import { clientLogProtobuf } from '@weberus/client-tracker/utils';
import { useAtomValue } from 'jotai';
import { goToHome } from '@/app/_shared/utils/go-to-home';
import config from '@/conf';
import { forceBackFromExternal } from '@/app/_shared/utils/navigation';
import { navigateTo } from '@/app/_shared/utils/action-navigator';
import { SearchHomeTabName, UniversalSearchCategory } from '@/app/(pages)/results/constants';
import { ProductListPageLog } from '@/app/_shared/protobuf-log/clientlogs/product-list-web-txtpb';
import { selectedVerticalTabInfoAtom } from '@/app/_shared/store/universal-search';
import { localCheckIOCapacityAtom } from '@/app/_shared/store/local-accommodation';
import { globalCheckIOCapacityAtom } from '@/app/_shared/store/global-accommodation';
import { buildCommonLog } from '@/app/(pages)/results/utils/build-common-log';

interface HeaderProps {
  keyword?: string;
  category?: CategoryType;
}

const leftButton = {
  'aria-label': 'Back',
  icon: <IcArrowback />,
};

const rightButton = {
  type: 'Icon',
  button: {
    'aria-label': 'Home',
    icon: <IcHome />,
  },
};

export const NavBar = ({ keyword = '', category = UniversalSearchCategory.LOCAL_ACCOMMODATION }: HeaderProps) => {
  const selectedTabInfo = useAtomValue(selectedVerticalTabInfoAtom);
  const localCheckIOCapacity = useAtomValue(localCheckIOCapacityAtom);
  const globalCheckIOCapacity = useAtomValue(globalCheckIOCapacityAtom);

  const handleClickInput = (keyword?: string) => {
    clientLogProtobuf(ProductListPageLog.ButtonSearch.click, {
      logMeta: {
        ...selectedTabInfo?.serverLogMeta,
        ...buildCommonLog(selectedTabInfo?.key, localCheckIOCapacity, globalCheckIOCapacity),
      },
    })();

    const searchHomeTab = SearchHomeTabName[category as keyof typeof SearchHomeTabName];
    const link = `search?tab=${searchHomeTab}${keyword ? `&keyword=${encodeURIComponent(keyword)}` : ''}`;
    navigateTo({ app: `yanoljamotel://${link}`, web: `${config.service.home.origin}/${link}` });
  };

  const handleClickBack = () => {
    clientLogProtobuf(ProductListPageLog.SearchButtonBackward.click, {
      logMeta: {
        ...selectedTabInfo?.serverLogMeta,
        ...buildCommonLog(selectedTabInfo?.key, localCheckIOCapacity, globalCheckIOCapacity),
      },
    })();
    if (yaNative.isEnabled) {
      yaNative.dismiss();
    } else {
      forceBackFromExternal('/');
    }
  };

  const handleClickHome = () => {
    clientLogProtobuf(ProductListPageLog.SearchButtonHome.click, {
      logMeta: {
        ...selectedTabInfo?.serverLogMeta,
        ...buildCommonLog(selectedTabInfo?.key, localCheckIOCapacity, globalCheckIOCapacity),
      },
    })();
    goToHome();
  };

  return (
    <TopNavigation
      leftButton={{ ...leftButton, onClick: handleClickBack }}
      pcType="pcLegacySize"
      deviceType="android"
      rightButtons={[
        {
          ...rightButton,
          button: { ...rightButton.button, onClick: handleClickHome },
        } as TopNavigationRightButtonProps,
      ]}>
      <div className="flex items-center">
        <div
          className="flex h-40 w-0 flex-grow cursor-pointer items-center justify-between rounded-8 bg-fill-neutral-weak1 py-4 pl-16 pr-10"
          onClick={() => {
            handleClickInput(keyword);
          }}>
          <div className="truncate text-fill-neutral-main typography-subtitle-16-bold">{keyword}</div>
          <div className="flex min-h-32 min-w-32 items-center justify-center">
            <IcDeleteFill
              className="h-20 w-20 text-fill-neutral-weak4 hover:text-fill-neutral-medium"
              onClick={(e) => {
                e.stopPropagation();
                handleClickInput();
              }}
            />
          </div>
        </div>
      </div>
    </TopNavigation>
  );
};
